import {
    AuctionsNearMeGeoAreaParams,
    getAuctionsNearMePageUrl,
    getCatalogUrl,
    getMediumItemImageUrl,
    mobileLogo,
} from '@/utils/urls';
import { EmptyCoverLot } from '@/types/CoverLot';
import { escapeQuotes } from '@/utils/escapeQuotes';
import { formatHyphenatedDateOnly } from '@liveauctioneers/caterwaul-components/lib/utils/dates';
import { GeoArea } from '@/types/GeoArea';
import { getAllCatalogs } from '@/redux/modules/catalog/catalogs/catalog.selectors';
import { getBaseUrl } from '@/redux/modules/config';
import { getSsrAuctionsNearMeHitsSelector } from '@/redux/modules/auctionsNearMe/auctionsNearMe.selectors';
import { useAppSelector } from '@/redux/hooks';
import { useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import isEqual from 'react-fast-compare';
import PageHeaderMeta from '@liveauctioneers/caterwaul-components/lib/PageHeaderMeta/PageHeaderMeta';
import React, { ReactElement } from 'react';
import useAddNotificationCountToTitle from '@/hooks/useAddNotificationCountToTitle';

export type AuctionsNearMePageMetaProps = {
    geoArea?: GeoArea;
    locationName?: string;
    searchTerm?: string;
};

const AuctionsNearMePageHeaderMeta = ({
    geoArea,
    locationName,
    searchTerm,
}: AuctionsNearMePageMetaProps): ReactElement => {
    const { formatMessage } = useIntl();
    const baseUrl = useAppSelector(getBaseUrl, isEqual);
    const catalogsById = useAppSelector(getAllCatalogs);

    let title = formatMessage({ id: 'near_me_page_title' });
    if (geoArea?.pageTitle) {
        title = geoArea.pageTitle;
    } else if (locationName) {
        title = `${formatMessage({ id: 'auctionsIn' })} ${locationName}`;
    }

    title = useAddNotificationCountToTitle(title);

    const description =
        geoArea?.description ||
        `${formatMessage({ id: 'near_me_page_description' })} ${locationName ? locationName : 'North America'}`;
    const geoAreaData: AuctionsNearMeGeoAreaParams | undefined = geoArea
        ? {
              geoAreaId: geoArea.geoAreaId,
              geoAreaName: geoArea.geoAreaName,
          }
        : undefined;
    const canonical = `${baseUrl}${getAuctionsNearMePageUrl(geoAreaData)}`;

    const auctionHits = useAppSelector(getSsrAuctionsNearMeHitsSelector);

    const auctionHitsMetaData = auctionHits
        .filter((hit) => Boolean(catalogsById[hit.Catalog.ID]))
        .map((hit) => {
            const { address, coverLots, saleEndTs, saleStartTs } = catalogsById[hit.Catalog.ID];
            const { address1, address2, postalCode } = address;
            const displayAddress = address2 ? `${address1} ${address2}` : `${address1}`;
            const [first = EmptyCoverLot] = coverLots;
            const imageUrl =
                getMediumItemImageUrl(hit.House.ID, hit.Catalog.ID, first.itemId, 1, first.imageVersion) || mobileLogo;
            return (
                <script
                    key={`${hit.objectID}`}
                    type="application/ld+json"
                >
                    {`
        {
            "@context": "https://schema.org",
            "@type": "SaleEvent",
            "description": "${escapeQuotes(hit.Catalog.Descr)}",
            "endDate": "${formatHyphenatedDateOnly(saleEndTs)}",
            "image": "${imageUrl}",
            "location": {
                "@type": "Place",
                "address": {
                    "@type": "PostalAddress",
                    "addressCountry": "${hit.Location.Country}",
                    "addressLocality": "${hit.Location.City}",
                    "addressRegion": "${hit.Location.State}",
                    "streetAddress": "${escapeQuotes(displayAddress)}",
                    "postalCode": "${postalCode}"
                },
                "name": "${hit.House.Name}"
            },
            "name": "${escapeQuotes(hit.Catalog.Title)}",
            "organizer": {
                "@type": "Organization",
                "address": {
                    "@type": "PostalAddress",
                    "addressCountry": "${hit.Location.Country}",
                    "addressLocality": "${hit.Location.City}",
                    "addressRegion": "${hit.Location.State}",
                    "streetAddress": "${displayAddress}",
                    "postalCode": "${postalCode}"
                },
                "name": "${hit.House.Name}"
            },
            "startDate": "${formatHyphenatedDateOnly(saleStartTs)}",
            "url": "${getCatalogUrl(hit.Catalog.ID, hit.Catalog.Title)}"
        }
    `}
                </script>
            );
        });

    // FF_SG229_NEW_AUCTIONS_NEAR_ME_PAGE
    // Custom search pages should not be indexed by search engines
    const isCustomSearch = Boolean(searchTerm);

    return (
        <PageHeaderMeta
            canonical={canonical}
            description={description}
            title={title}
        >
            {isCustomSearch && (
                <meta
                    content="noindex, follow"
                    name="robots"
                />
            )}
            {auctionHitsMetaData}
        </PageHeaderMeta>
    );
};

export default AuctionsNearMePageHeaderMeta;
